import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "@clerk/clerk-react";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3001";

export interface SubLogItem {
  id: string;
  message: string;
  status: "pending" | "in_progress" | "completed" | "error";
}

export interface AnalysisLog {
  id: string;
  timestamp: string;
  stage: string;
  status: "started" | "progress" | "completed" | "error";
  message: string;
  progress?: number;
  details?: string;
  subLogs?: SubLogItem[];
}

export interface AnalysisStatus {
  projectStatus: "analyzing" | "ready" | "error";
  progress: number;
  totalPages?: number;
  error?: string;
}

interface UseAnalysisLogsOptions {
  enabled?: boolean;
  onError?: (error: Error) => void;
  onComplete?: () => void;
}

export function useAnalysisLogs(
  projectId: string,
  options: UseAnalysisLogsOptions = {}
) {
  const { enabled = true, onError, onComplete } = options;
  const { getToken } = useAuth();
  const [logs, setLogs] = useState<AnalysisLog[]>([]);
  const [status, setStatus] = useState<AnalysisStatus | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState<Error | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearLogs = useCallback(() => {
    setLogs([]);
    setStatus(null);
  }, []);

  useEffect(() => {
    if (!enabled || !projectId) {
      // Close existing connection when disabled
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        setIsConnected(false);
      }
      return;
    }

    let isMounted = true;

    const connect = async () => {
      try {
        // Get auth token
        const token = await getToken();
        if (!token) {
          setConnectionError(new Error("Not authenticated"));
          return;
        }

        // Close existing connection if any
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }

        // Create SSE connection with token as query param
        const url = `${API_URL}/projects/${projectId}/analysis/logs?token=${encodeURIComponent(token)}`;
        const eventSource = new EventSource(url);
        eventSourceRef.current = eventSource;

        eventSource.onopen = () => {
          if (isMounted) {
            setIsConnected(true);
            setConnectionError(null);
          }
        };

        eventSource.onerror = () => {
          if (isMounted) {
            setIsConnected(false);
            // Don't set error for normal close
            if (eventSource.readyState === EventSource.CLOSED) {
              return;
            }
            const error = new Error("SSE connection failed");
            setConnectionError(error);
            onError?.(error);

            // Try to reconnect after 3 seconds
            if (reconnectTimeoutRef.current) {
              clearTimeout(reconnectTimeoutRef.current);
            }
            reconnectTimeoutRef.current = setTimeout(() => {
              if (isMounted && enabled) {
                connect();
              }
            }, 3000);
          }
        };

        // Handle log messages
        eventSource.addEventListener("log", (event) => {
          if (!isMounted) return;
          try {
            const log: AnalysisLog = JSON.parse(event.data);

            setLogs((prev) => {
              // For "started" logs - always add as new entry
              if (log.status === "started") {
                return [...prev, log];
              }

              // For "completed" logs - update the in-progress entry and add completion as new entry
              if (log.status === "completed") {
                const updated = [...prev];
                // Find the entry for this stage (could be "started" or "progress") and mark it as completed
                const inProgressIndex = updated.findIndex(
                  (l) => l.stage === log.stage && (l.status === "started" || l.status === "progress")
                );
                if (inProgressIndex >= 0) {
                  // Mark it as completed and clear subLogs (they're all done)
                  updated[inProgressIndex] = {
                    ...updated[inProgressIndex],
                    status: "completed",
                    subLogs: updated[inProgressIndex].subLogs?.map(s => ({ ...s, status: "completed" as const })),
                  };
                }
                // Add the completion message as a new entry
                return [...updated, log];
              }

              // For "progress" logs with subLogs - update existing entry for this stage
              // Keep the original log id to prevent component remounting and animation re-triggering
              if (log.status === "progress" && log.subLogs) {
                const existingIndex = prev.findIndex(
                  (l) => l.stage === log.stage && (l.status === "started" || l.status === "progress")
                );
                if (existingIndex >= 0) {
                  const updated = [...prev];
                  // Preserve the original id to prevent React from remounting the component
                  updated[existingIndex] = {
                    ...log,
                    id: prev[existingIndex].id,
                  };
                  return updated;
                }
              }

              // Default: add as new log
              return [...prev, log];
            });

            // Check for completion
            if (log.stage === "complete" && log.status === "completed") {
              onComplete?.();
            }
          } catch (e) {
            console.error("Failed to parse log event:", e);
          }
        });

        // Handle heartbeat (just for connection status)
        eventSource.addEventListener("heartbeat", () => {
          if (isMounted) {
            setIsConnected(true);
          }
        });

        // Handle connection event
        eventSource.addEventListener("connected", () => {
          if (isMounted) {
            setIsConnected(true);
          }
        });

        // Handle status updates for progress bar
        eventSource.addEventListener("status", (event) => {
          if (!isMounted) return;
          try {
            const statusData: AnalysisStatus = JSON.parse(event.data);
            setStatus(statusData);

            // Trigger onComplete when status changes to ready
            if (statusData.projectStatus === "ready") {
              onComplete?.();
            }
          } catch (e) {
            console.error("Failed to parse status event:", e);
          }
        });
      } catch (e) {
        if (isMounted) {
          const error = e instanceof Error ? e : new Error("Connection failed");
          setConnectionError(error);
          onError?.(error);
        }
      }
    };

    connect();

    return () => {
      isMounted = false;
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      setIsConnected(false);
    };
  }, [projectId, enabled, getToken, onError, onComplete]);

  return {
    logs,
    status,
    isConnected,
    connectionError,
    clearLogs,
    latestLog: logs[logs.length - 1] || null,
  };
}
